/**
 * KnowledgeFromIssueModal
 *
 * Modal for creating domain knowledge directly from a content issue.
 * Pre-fills the form based on issue context and recommended solution.
 *
 * Part of Story 48: Prescriptive Issue Resolution with Self-Service Knowledge Creation
 *
 * Date: December 22, 2025
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import {
  Loader2,
  AlertTriangle,
  Lightbulb,
  Zap,
  BookOpen,
  CheckCircle,
  RefreshCw,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import {
  ContentIssue,
} from '@/services/contentReviewService';
import {
  KnowledgeType,
  ScopeLevel,
  ResearchMethodology,
  createKnowledge,
  updateKnowledgeStatus,
  queueRegeneration,
} from '@/services/domainKnowledgeService';
import { RegenerationProgress } from './RegenerationProgress';

interface ProductContext {
  id: string;
  title: string;
  category_path?: string;
  brand?: string;
  contentType: string;
}

interface KnowledgeFromIssueModalProps {
  open: boolean;
  onClose: () => void;
  issue: ContentIssue;
  issueIndex: number;
  productContext: ProductContext;
  evaluationId?: string;
  onCreated: (knowledgeId: string) => void;
}

const KNOWLEDGE_TYPES: { value: KnowledgeType; label: string }[] = [
  { value: 'terminology', label: 'Terminology' },
  { value: 'specification', label: 'Specification' },
  { value: 'context', label: 'Context' },
  { value: 'expertise_signal', label: 'Expertise Signal' },
  { value: 'validation_rule', label: 'Validation Rule' },
];

const SCOPE_LEVELS: { value: ScopeLevel; label: string }[] = [
  { value: 'sector', label: 'Global (Sector)' },
  { value: 'category', label: 'Category' },
  { value: 'brand', label: 'Brand' },
];

const RESEARCH_METHODOLOGIES: { value: ResearchMethodology; label: string }[] = [
  { value: 'EXPERT_INPUT', label: 'Manual Entry' },
  { value: 'WEB_SEARCH', label: 'Web Search' },
  { value: 'MANUFACTURER_SPECS', label: 'Manufacturer Specs' },
  { value: 'INDUSTRY_STANDARDS', label: 'Industry Standards' },
];

export function KnowledgeFromIssueModal({
  open,
  onClose,
  issue,
  issueIndex,
  productContext,
  evaluationId,
  onCreated,
}: KnowledgeFromIssueModalProps) {
  const { selectedSite } = useSite();
  const [loading, setLoading] = useState(false);
  const [impactPreview, setImpactPreview] = useState<number | null>(null);
  const [loadingImpact, setLoadingImpact] = useState(false);

  // Story 48: Auto-approve and regeneration options
  const [autoApprove, setAutoApprove] = useState(false);
  const [triggerRegeneration, setTriggerRegeneration] = useState(false);
  const [regenerationJobId, setRegenerationJobId] = useState<string | null>(null);
  const [createdKnowledgeId, setCreatedKnowledgeId] = useState<string | null>(null);

  // Form state - pre-filled from issue
  const [formData, setFormData] = useState({
    title: '',
    knowledge_text: '',
    knowledge_type: 'context' as KnowledgeType,
    scope_level: 'category' as ScopeLevel,
    scope_value: '',
    research_question: '',
    research_methodology: 'EXPERT_INPUT' as ResearchMethodology,
  });

  // Pre-fill form when modal opens
  useEffect(() => {
    if (open && issue) {
      const solution = issue.recommended_solution;

      // Generate title from issue description
      const titleBase = issue.description.length > 60
        ? issue.description.substring(0, 60) + '...'
        : issue.description;
      const title = `${getIssueTypePrefix(issue.type)}: ${titleBase}`;

      // Map knowledge type
      const knowledgeType = solution?.knowledge_type || mapIssueTypeToKnowledgeType(issue.type);

      // Determine scope
      let scopeLevel: ScopeLevel = 'sector';
      let scopeValue = '';

      if (solution?.suggested_scope) {
        scopeLevel = solution.suggested_scope.level as ScopeLevel;
        scopeValue = solution.suggested_scope.value || '';
      } else if (productContext.category_path) {
        scopeLevel = 'category';
        const categories = productContext.category_path.split(/[>/,]/).map(c => c.trim()).filter(Boolean);
        scopeValue = categories[categories.length - 1] || '';
      } else if (productContext.brand) {
        scopeLevel = 'brand';
        scopeValue = productContext.brand;
      }

      // Generate knowledge text template
      const knowledgeText = generateKnowledgeTemplate(issue, productContext);

      // Generate research question
      const researchQuestion = issue.suggestion
        ? `How should "${issue.suggestion.substring(0, 100)}" be implemented consistently?`
        : `What is the correct standard for ${issue.type.toLowerCase()} in this context?`;

      setFormData({
        title,
        knowledge_text: knowledgeText,
        knowledge_type: knowledgeType,
        scope_level: scopeLevel,
        scope_value: scopeValue,
        research_question: researchQuestion,
        research_methodology: 'EXPERT_INPUT',
      });

      // Set initial impact preview from solution
      if (solution?.estimated_impact !== undefined) {
        setImpactPreview(solution.estimated_impact);
      }
    }
  }, [open, issue, productContext]);

  // Recalculate impact when scope changes
  useEffect(() => {
    if (open && formData.scope_level && selectedSite?.id) {
      loadImpactPreview();
    }
  }, [formData.scope_level, formData.scope_value, selectedSite?.id]);

  const loadImpactPreview = async () => {
    if (!selectedSite?.id) return;

    setLoadingImpact(true);
    try {
      const response = await fetch(
        `/api/domain-knowledge/impact-preview?` +
        `siteId=${selectedSite.id}&` +
        `scopeLevel=${formData.scope_level}&` +
        `scopeValue=${encodeURIComponent(formData.scope_value || '')}`
      );
      const data = await response.json();
      if (data.success) {
        setImpactPreview(data.totalProducts || 0);
      }
    } catch (err) {
      console.warn('[KnowledgeFromIssue] Could not load impact preview:', err);
    } finally {
      setLoadingImpact(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Title is required',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedSite?.id) {
      toast({
        title: 'Validation Error',
        description: 'Please select a site first',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const data: Record<string, unknown> = {
        siteId: selectedSite.id,
        title: formData.title.trim(),
        knowledgeText: formData.knowledge_text.trim() || undefined,
        knowledgeType: formData.knowledge_type,
        scopeLevel: formData.scope_level,
        researchQuestion: formData.research_question.trim() || undefined,
        researchMethodology: formData.research_methodology,
        // Link to source issue (Task 48.5)
        source_issue: evaluationId ? {
          evaluation_id: evaluationId,
          issue_index: issueIndex,
        } : undefined,
      };

      // Add scope-specific fields
      switch (formData.scope_level) {
        case 'category':
          data.scopeCategoryPath = formData.scope_value || undefined;
          break;
        case 'brand':
          data.scopeBrand = formData.scope_value || undefined;
          break;
      }

      const response = await createKnowledge(data);

      if (response.success && response.data) {
        const knowledgeId = response.data.id;
        setCreatedKnowledgeId(knowledgeId);

        // Step 2: Auto-approve if requested
        if (autoApprove) {
          const approveResponse = await updateKnowledgeStatus(knowledgeId, 'approved');
          if (!approveResponse.success) {
            toast({
              title: 'Warning',
              description: 'Knowledge created but auto-approval failed: ' + approveResponse.error,
              variant: 'destructive',
            });
          }

          // Step 3: Trigger regeneration if requested
          if (triggerRegeneration && approveResponse.success) {
            const regenResponse = await queueRegeneration(knowledgeId, { dryRun: false });
            if (regenResponse.success && regenResponse.data && 'jobId' in regenResponse.data) {
              setRegenerationJobId(regenResponse.data.jobId);
              toast({
                title: 'Regeneration Queued',
                description: `Started regenerating ${regenResponse.data.totalItems} items`,
              });
              // Keep modal open to show progress
              return;
            } else if (!regenResponse.success) {
              toast({
                title: 'Warning',
                description: 'Knowledge approved but regeneration failed: ' + regenResponse.error,
                variant: 'destructive',
              });
            }
          }
        }

        toast({
          title: 'Knowledge Created',
          description: autoApprove
            ? 'Domain knowledge approved and active. It will be applied to future content generation.'
            : 'Domain knowledge entry created. It needs approval before it takes effect.',
        });
        onCreated(knowledgeId);
        onClose();
      } else {
        toast({
          title: 'Error',
          description: response.error || 'Failed to create knowledge',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('[KnowledgeFromIssue] Error creating:', err);
      toast({
        title: 'Error',
        description: 'Failed to create knowledge entry',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerationComplete = () => {
    if (createdKnowledgeId) {
      onCreated(createdKnowledgeId);
    }
    onClose();
  };

  // If regeneration is in progress, show progress view
  if (regenerationJobId) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && handleRegenerationComplete()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />
              Content Regeneration
            </DialogTitle>
            <DialogDescription>
              Regenerating content with the new knowledge...
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <RegenerationProgress
              jobId={regenerationJobId}
              onComplete={handleRegenerationComplete}
              onCancel={handleRegenerationComplete}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleRegenerationComplete}>
              Continue in Background
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-purple-500" />
            Create Domain Knowledge from Issue
          </DialogTitle>
          <DialogDescription>
            This knowledge will teach the AI system how to avoid similar issues in future content generation.
          </DialogDescription>
        </DialogHeader>

        {/* Source Issue Context */}
        <div className="bg-muted rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            <span className="text-sm font-medium">Source Issue</span>
            <Badge variant="outline" className="text-xs">{issue.type}</Badge>
            <Badge variant={issue.severity === 'HIGH' ? 'destructive' : 'secondary'} className="text-xs">
              {issue.severity}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{issue.description}</p>
          {issue.location && (
            <p className="text-xs text-muted-foreground mt-1 italic">"{issue.location}"</p>
          )}
          {issue.suggestion && (
            <p className="text-sm text-blue-600 mt-2 flex items-center gap-1">
              <Lightbulb className="h-3 w-3" />
              {issue.suggestion}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Product: {productContext.title}
          </p>
        </div>

        <Separator />

        <div className="space-y-4 py-4">
          {/* Title */}
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              placeholder="Brief descriptive title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
            />
          </div>

          {/* Knowledge Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="knowledge_type">Knowledge Type</Label>
              <Select
                value={formData.knowledge_type}
                onValueChange={(v) => handleChange('knowledge_type', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {KNOWLEDGE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Scope Level */}
            <div>
              <Label htmlFor="scope_level">Scope</Label>
              <Select
                value={formData.scope_level}
                onValueChange={(v) => handleChange('scope_level', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select scope" />
                </SelectTrigger>
                <SelectContent>
                  {SCOPE_LEVELS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Scope Value */}
          {formData.scope_level !== 'sector' && (
            <div>
              <Label htmlFor="scope_value">
                {formData.scope_level === 'category' ? 'Category' : 'Brand'} Name
              </Label>
              <Input
                id="scope_value"
                placeholder={formData.scope_level === 'category' ? 'e.g., Wheels, Ruedas' : 'e.g., Zipp'}
                value={formData.scope_value}
                onChange={(e) => handleChange('scope_value', e.target.value)}
              />
            </div>
          )}

          {/* Impact Preview */}
          <div className="bg-purple-50 dark:bg-purple-950/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-purple-500" />
                <span className="text-sm font-medium">Estimated Impact</span>
              </div>
              {loadingImpact ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Badge variant="secondary">
                  ~{impactPreview || 0} products affected
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              This knowledge will influence AI content generation for products matching this scope.
            </p>
          </div>

          {/* Knowledge Text */}
          <div>
            <Label htmlFor="knowledge_text">Knowledge Content</Label>
            <Textarea
              id="knowledge_text"
              placeholder="The rule or knowledge the AI should follow..."
              rows={5}
              value={formData.knowledge_text}
              onChange={(e) => handleChange('knowledge_text', e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Write clear instructions that the AI can follow when generating content.
            </p>
          </div>

          {/* Research Question (collapsed by default) */}
          <details className="group">
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              Advanced: Research Settings
            </summary>
            <div className="mt-3 space-y-3 pl-2 border-l-2 border-muted">
              <div>
                <Label htmlFor="research_question">Research Question</Label>
                <Textarea
                  id="research_question"
                  placeholder="Optional: What should be researched to verify this?"
                  rows={2}
                  value={formData.research_question}
                  onChange={(e) => handleChange('research_question', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="research_methodology">Research Method</Label>
                <Select
                  value={formData.research_methodology}
                  onValueChange={(v) => handleChange('research_methodology', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RESEARCH_METHODOLOGIES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </details>
        </div>

        {/* Auto-approve and Regeneration Options */}
        <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="autoApprove"
              checked={autoApprove}
              onCheckedChange={(checked) => {
                setAutoApprove(!!checked);
                if (!checked) setTriggerRegeneration(false);
              }}
            />
            <Label htmlFor="autoApprove" className="text-sm font-medium cursor-pointer">
              Approve immediately (skip review)
            </Label>
          </div>

          {autoApprove && impactPreview && impactPreview > 0 && (
            <div className="flex items-center space-x-2 ml-6">
              <Checkbox
                id="triggerRegeneration"
                checked={triggerRegeneration}
                onCheckedChange={(checked) => setTriggerRegeneration(!!checked)}
              />
              <Label htmlFor="triggerRegeneration" className="text-sm cursor-pointer">
                <span className="flex items-center gap-1">
                  <RefreshCw className="h-3 w-3" />
                  Regenerate ~{impactPreview} affected products
                </span>
              </Label>
            </div>
          )}

          {triggerRegeneration && (
            <p className="text-xs text-muted-foreground ml-6">
              This will queue regeneration of all products in scope. You can monitor progress in the background.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4 mr-1" />
            )}
            {autoApprove ? 'Create & Approve' : 'Create Knowledge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Helper functions

function getIssueTypePrefix(type: string): string {
  const prefixes: Record<string, string> = {
    TERMINOLOGY: 'Term',
    SPECIFICATION: 'Spec',
    MISSING_CONTEXT: 'Context',
    EXPERTISE_SIGNAL: 'Expert',
    HALLUCINATION: 'Data',
    LOGIC_ERROR: 'Logic',
  };
  return prefixes[type] || 'Issue';
}

function mapIssueTypeToKnowledgeType(issueType: string): KnowledgeType {
  const mapping: Record<string, KnowledgeType> = {
    TERMINOLOGY: 'terminology',
    SPECIFICATION: 'specification',
    MISSING_CONTEXT: 'context',
    EXPERTISE_SIGNAL: 'expertise_signal',
    HALLUCINATION: 'validation_rule',
    LOGIC_ERROR: 'validation_rule',
  };
  return mapping[issueType] || 'context';
}

function generateKnowledgeTemplate(issue: ContentIssue, context: ProductContext): string {
  const lines: string[] = [];

  // Add issue context
  lines.push(`[Issue]: ${issue.description}`);

  if (issue.location) {
    lines.push(`[Location]: "${issue.location}"`);
  }

  if (issue.suggestion) {
    lines.push(`[Standard]: ${issue.suggestion}`);
  }

  lines.push('');
  lines.push('[Rule]: ');
  lines.push('(Write the rule that the AI should follow here)');

  return lines.join('\n');
}
